import React, { useState } from 'react';
import {
  ArrowUpToLine, ArrowRightToLine, Move3d, RefreshCcw, MountainSnow,
  SlidersHorizontal, Layers3,
  Upload, Database,
  ImageDown, FileDown,
  LayoutGrid, Type, Maximize2,
} from 'lucide-react';
import { RibbonGroup, RibbonBtn, RibbonDivider } from './GisRibbon';
import { useTheme } from '../contexts/ThemeContext';

// ─── Display modes for ribbon buttons (shared look/feel with the 2D ribbon) ───
const DISPLAY_MODES = [
  { id: 'both', label: 'Icon + Name', icon: LayoutGrid },
  { id: 'icon', label: 'Icon Only', icon: Maximize2 },
  { id: 'name', label: 'Name Only', icon: Type },
];

// ─── Tab definitions ───
const TABS = [
  { id: 'view', label: 'View' },
  { id: 'volume', label: 'Volume' },
  { id: 'insert', label: 'Insert' },
  { id: 'reports', label: 'Reports' },
  { id: 'share', label: 'Share' },
];

const COLOR_MODES = [
  { id: 'viridis', label: 'Viridis (Depth)' },
  { id: 'height', label: 'Height Ramp' },
  { id: 'rgb', label: 'RGB' },
  { id: 'intensity', label: 'Intensity' },
  { id: 'classification', label: 'Classification' },
];

// ─── Ribbon content per tab ───
function ViewRibbon({ onCameraPreset, colorMode, setColorMode, showTerrain, onToggleTerrain, displayMode }) {
  const { C } = useTheme();
  return (
    <div className="flex items-center gap-1 px-3 h-[38px] overflow-x-auto">
      <RibbonGroup label="Camera">
        <RibbonBtn icon={ArrowUpToLine} label="Top" onClick={() => onCameraPreset('top')} displayMode={displayMode} />
        <RibbonBtn icon={ArrowRightToLine} label="Side" onClick={() => onCameraPreset('side')} displayMode={displayMode} />
        <RibbonBtn icon={Move3d} label="Perspective" onClick={() => onCameraPreset('perspective')} displayMode={displayMode} />
        <RibbonBtn icon={RefreshCcw} label="Reset View" onClick={() => onCameraPreset('reset')} displayMode={displayMode} />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Color Mode">
        <select value={colorMode} onChange={e => setColorMode(e.target.value)}
          className="h-7 px-2 text-[11px] rounded-md outline-none"
          style={{ background: C.bg4, border: `1px solid ${C.border}`, color: C.text0 }}>
          {COLOR_MODES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Scene">
        <RibbonBtn icon={MountainSnow} label="Terrain" active={showTerrain} onClick={onToggleTerrain} tone="amber" displayMode={displayMode} />
      </RibbonGroup>
    </div>
  );
}

function VolumeRibbon({ showVolumePanel, onToggleVolumePanel, onResetVolumeFilters, showFences, onToggleFences, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[38px] overflow-x-auto">
      <RibbonGroup label="Panel">
        <RibbonBtn icon={SlidersHorizontal} label="Volume Panel" active={showVolumePanel} onClick={onToggleVolumePanel} displayMode={displayMode} size="lg" />
      </RibbonGroup>

      <RibbonDivider />

      <RibbonGroup label="Filters">
        <RibbonBtn icon={RefreshCcw} label="Reset Filters" onClick={onResetVolumeFilters} displayMode={displayMode} />
        <RibbonBtn icon={Layers3} label="Fence Diagrams" active={showFences} onClick={onToggleFences} displayMode={displayMode} />
      </RibbonGroup>
    </div>
  );
}

function InsertRibbon({ onImportInversionModel, onAddData, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[38px] overflow-x-auto">
      <RibbonGroup label="Data">
        <RibbonBtn icon={Upload} label="Import Inversion Model" onClick={onImportInversionModel} displayMode={displayMode} size="lg" />
        <RibbonBtn icon={Database} label="Add Data" onClick={onAddData} displayMode={displayMode} size="lg" />
      </RibbonGroup>
    </div>
  );
}

function ReportsRibbon({ onExportImage, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[38px] overflow-x-auto">
      <RibbonGroup label="Export">
        <RibbonBtn icon={ImageDown} label="Export Image" onClick={onExportImage} displayMode={displayMode} size="lg" />
      </RibbonGroup>
    </div>
  );
}

function ShareRibbon({ onExportModel, displayMode }) {
  return (
    <div className="flex items-center gap-1 px-3 h-[38px] overflow-x-auto">
      <RibbonGroup label="Export">
        <RibbonBtn icon={FileDown} label="Export Model" onClick={onExportModel} displayMode={displayMode} size="lg" />
      </RibbonGroup>
    </div>
  );
}

// ─── Main Ribbon Component ───
export default function Viewer3DRibbon(props) {
  const { C } = useTheme();
  const [activeTab, setActiveTab] = useState('view');
  const [displayMode, setDisplayMode] = useState(() => localStorage.getItem('ribbon-display') || 'both');

  const cycleDisplay = () => {
    const modes = ['both', 'icon', 'name'];
    const next = modes[(modes.indexOf(displayMode) + 1) % modes.length];
    setDisplayMode(next);
    localStorage.setItem('ribbon-display', next);
  };

  const CurrentModeIcon = DISPLAY_MODES.find(m => m.id === displayMode)?.icon || LayoutGrid;

  const renderContent = () => {
    switch (activeTab) {
      case 'view': return <ViewRibbon {...props} displayMode={displayMode} />;
      case 'volume': return <VolumeRibbon {...props} displayMode={displayMode} />;
      case 'insert': return <InsertRibbon {...props} displayMode={displayMode} />;
      case 'reports': return <ReportsRibbon {...props} displayMode={displayMode} />;
      case 'share': return <ShareRibbon {...props} displayMode={displayMode} />;
      default: return null;
    }
  };

  return (
    <div className="flex flex-col" style={{ background: C.bg1, borderBottom: `1px solid ${C.border}` }}>
      {/* Tab bar */}
      <div className="flex items-center h-8 px-2 gap-0" style={{ borderBottom: `1px solid ${C.border}` }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 h-full text-[12px] font-medium border-b-2 transition-colors cursor-pointer
              ${activeTab === tab.id
                ? 'text-accent border-accent'
                : 'border-transparent hover:text-slate-200 hover:border-slate-500'}`}
            style={activeTab === tab.id ? undefined : { color: C.text3 }}
          >
            {tab.label}
          </button>
        ))}

        {/* Display mode toggle (right-aligned) */}
        <div className="ml-auto flex items-center gap-1">
          <button
            onClick={cycleDisplay}
            title={`Display: ${DISPLAY_MODES.find(m => m.id === displayMode)?.label} — click to cycle`}
            className="flex items-center gap-1 px-2 h-6 rounded text-[10px] hover:text-slate-300 hover:bg-white/5 transition-colors cursor-pointer border border-transparent hover:border-white/10"
            style={{ color: C.text3 }}
          >
            <CurrentModeIcon size={12} />
            <span className="hidden sm:inline">{DISPLAY_MODES.find(m => m.id === displayMode)?.label}</span>
          </button>
        </div>
      </div>

      {/* Ribbon content */}
      {renderContent()}
    </div>
  );
}
